/* eslint-disable @mizdra/layout-shift/require-size-attributes */
import React, { useEffect } from 'react';
import { bool, number, string } from 'prop-types';
import {
  customType,
  fragment,
  number as numberType,
  params,
  shape,
  string as stringType,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { useImpression } from '@thd-olt-component-react/impression';
import classNames from 'classnames/bind';
import {
  Button,
  Typography,
  Card,
  CardBody,
  CardMedia,
  CardTitle
} from '@one-thd/sui-atomic-components';
import styles from './spotlight.module.scss';

const Spotlight = ({
  componentId, componentClass, componentPosition, showDescription, boldTitle, horizontal
}) => {
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('spotlight.ready');
  }, []);

  const { data } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass
    },

  });

  const cx = classNames.bind(styles);

  const marketingData = data?.component?.marketingData;
  const {
    title: titleProductData,
    cta: ctaProductData,
    link: linkProductData,
    description: descriptionProductData
  } = data?.component || {};

  const {
    url: urlProductData,
  } = data?.component?.image?.damDownloadedContent || {};

  let title = titleProductData;
  let description = descriptionProductData;
  let link = linkProductData;
  let cta = ctaProductData;
  let url = urlProductData;

  const impressionProviderContextData = useImpression({ data: {
    id: componentId,
    name: Spotlight.displayName,
    component: Spotlight.displayName,
    position: componentPosition,
    type: 'content'
  } });
  if (marketingData) {
    const imageData = data?.component?.marketingData.lifestyleImage;

    title = marketingData.title_LessThan50Char;
    cta = marketingData.cta;
    link = marketingData.link;
    description = marketingData.description;
    url = imageData?.damDownloadedContent?.url;
  }

  return (
    <div
      data-component-id={componentId}
      id={`${componentClass}-${componentPosition}-${componentId}`}
      data-component-name="Spotlight"
      data-component-position={componentPosition}
      className={cx('spotlight--grid')}
      ref={impressionProviderContextData.ref}
      clickid={impressionProviderContextData.clickID}
    >
      <Card>
        <CardMedia
          src={url}
          aspect="wide"
          alt={`Image for ${title}`}
          title={`Image for ${title}`}
        />
        <CardTitle header={title} />
        <CardBody>
          {showDescription && description
            && (
              <Typography height="normal" weight="regular" variant="body-base" color="primary">
                {description}
              </Typography>
            )}
        </CardBody>
        <Button href={link} variant="secondary">{cta || ''}</Button>
      </Card>
    </div>
  );
};

Spotlight.propTypes = {
  componentId: string.isRequired,
  componentPosition: number,
  componentClass: string,
  showDescription: bool,
  boldTitle: bool,
  horizontal: bool
};

Spotlight.defaultProps = {
  componentPosition: 1,
  componentClass: '',
  showDescription: false,
  boldTitle: false,
  horizontal: false
};

Spotlight.displayName = 'Spotlight';

Spotlight.dataModel = {
  component: params({
    id: stringType().isRequired(),
    componentClass: customType('ComponentClass').enum(['Spotlight']).isRequired(),
  }).shape({
    Spotlight: fragment().shape({
      id: stringType(),
      title: stringType(),
      cta: stringType(),
      link: stringType(),
      componentName: stringType(),
      description: stringType(),
      image: shape({
        damDownloadedContent: shape({
          id: stringType(),
          url: stringType(),
          contentType: stringType(),
          title: stringType(),
          description: stringType(),
          details: shape({
            height: numberType(),
            width: numberType(),
            size: numberType(),
          }),
        }),
      }),
      marketingData: shape({
        id: stringType(),
        link: stringType(),
        title_LessThan50Char: stringType(),
        title_LessThan40Char: stringType(),
        description: stringType(),
        cta: stringType(),
        silohImage: shape({
          damDownloadedContent: shape({
            id: stringType(),
            url: stringType(),
            contentType: stringType(),
            title: stringType(),
            description: stringType(),
            details: shape({
              height: numberType(),
              width: numberType(),
              size: numberType(),
            }),
          }),
        }),
        lifestyleImage: shape({
          damDownloadedContent: shape({
            id: stringType(),
            url: stringType(),
            contentType: stringType(),
            title: stringType(),
            description: stringType(),
            details: shape({
              height: numberType(),
              width: numberType(),
              size: numberType(),
            }),
          }),
        }),
      }),
    }),
  }),
};

export { Spotlight };
